import { TrackingScheme } from 'tracking';
import videojs, { VideoJsPlayer } from 'video.js';

import {
  AdEventType,
  Event,
  LabelKey,
  LegacyTrackingEvent,
  TrackingEvent,
} from '@src/types/event';

const Plugin = videojs.getPlugin('plugin');

const DEFAULT_TRACKING_CATEGORY = 'PlayerV3';

export const PLUGIN_NAME = 'eventManagerTracker';

const eventLabelKeysToTrack = [
  LabelKey.SESSION_ID,
  LabelKey.VIDEO_ID,
  LabelKey.VIDEO_URL,
  LabelKey.VERSION,
  LabelKey.IS_LIVE,
  LabelKey.TS,
  LabelKey.URL,
  LabelKey.IS_PAUSED,
  LabelKey.IS_AUTO_PLAY,
  LabelKey.IS_MUTED,
  LabelKey.VIDEO_TIME,
  LabelKey.VIDEO_LENGTH,
  LabelKey.VIDEO_FROM_TIME,
  LabelKey.VIDEO_TO_TIME,
  LabelKey.BUTTON,
  LabelKey.AD_SEC_FROM_START,
  LabelKey.NUM_AD_STARTED,
  LabelKey.ADID,
  LabelKey.ADTYPE,
  // ad event related
  LabelKey.EVENT_TYPE,
  // ad error related
  LabelKey.AD_ERROR_CODE,
  LabelKey.AD_INNER_ERROR,
  LabelKey.AD_ERROR_MESSAGE,
  LabelKey.AD_ERROR_TYPE,
  LabelKey.AD_VAST_ERROR_CODE,
];

const defaultEventTypesToTrackMap = new Map<
  TrackingScheme,
  (TrackingEvent | LegacyTrackingEvent)[]
>();

defaultEventTypesToTrackMap.set(TrackingScheme.V3, [
  Event.CLICK,
  Event.REACH,
  LegacyTrackingEvent.STARTVIDEOAD,
  Event.START,
  Event.RESUME,
  Event.PAUSE,
  Event.SEEK,
  Event.HEARTBEAT,
]);

defaultEventTypesToTrackMap.set(TrackingScheme.V3_AD, [
  Event.CLICK,
  Event.REACH,
  Event.AD_EVENT,
  Event.START,
  Event.RESUME,
  Event.PAUSE,
  Event.SEEK,
  Event.HEARTBEAT,
]);

const defaultGetShouldEmitTracking = (evt: string, data?: unknown) => {
  if (evt === Event.AD_EVENT) {
    if (!data || typeof data !== 'object') return false;

    return (Object.values(AdEventType) as unknown[]).includes(
      (data as { [LabelKey.EVENT_TYPE]: unknown })[LabelKey.EVENT_TYPE]
    );
  }

  return true;
};

export interface Options {
  defaultTrackingCategory?: string;
  eventTypesToTrack?: string[];
}

class Tracker extends Plugin {
  defaultTrackingCategory: string;
  eventTypesToTrack: string[];
  getShouldEmitTracking: (evt: string, data: unknown) => boolean;

  constructor(player: VideoJsPlayer, pluginOptions?: Options) {
    super(player, pluginOptions);

    const playerOptions = player.options_ ?? {};

    playerOptions.trackingScheme === TrackingScheme.V3;

    this.eventTypesToTrack =
      player.options_?.eventTypesToTrack ?? // instance options override
      pluginOptions?.eventTypesToTrack ?? // plugin options override
      ((playerOptions.trackingScheme === TrackingScheme.V3 // default according to options.trackingScheme
        ? defaultEventTypesToTrackMap.get(TrackingScheme.V3)
        : defaultEventTypesToTrackMap.get(TrackingScheme.V3_AD)) as string[]);

    this.getShouldEmitTracking = defaultGetShouldEmitTracking;

    this.defaultTrackingCategory =
      playerOptions.defaultTrackingCategory ??
      pluginOptions?.defaultTrackingCategory ??
      DEFAULT_TRACKING_CATEGORY;

    this.init();
  }

  init() {
    this.eventTypesToTrack.forEach((eventType) =>
      this.on(eventType, (evt, data) => {
        // TODO check this.state.disableTracking before emit
        if (!this.getShouldEmitTracking(eventType, data)) return;

        this.emit(evt, data);

        this.player.options_?.trackingHandlers?.[evt.type]?.(evt);
      })
    );
  }

  prepareTrackingCategory(
    evt: videojs.EventTarget.Event,
    data?: Record<string, unknown>
  ) {
    return evt?.category ?? data?.category ?? this.defaultTrackingCategory;
  }

  prepareTrackingAction(
    evt: videojs.EventTarget.Event,
    data?: Record<string, unknown>
  ) {
    return evt?.action ?? data?.action ?? evt?.type;
  }

  prepareTrackingLabel(
    evt: videojs.EventTarget.Event,
    data?: Record<string, unknown>
  ) {
    const labelObj = eventLabelKeysToTrack.reduce<{ [key: string]: string }>(
      (obj, key) => {
        obj[key] = data?.[key] ?? evt[key];
        return obj;
      },
      {}
    );

    return labelObj;
  }

  emit(evt: videojs.EventTarget.Event, data: Record<string, unknown>) {
    if (!evt || !this.player.options_.onEvent) {
      return;
    }

    const category = this.prepareTrackingCategory(evt, data);
    const action = this.prepareTrackingAction(evt, data);
    const labelObj = this.prepareTrackingLabel(evt, data);

    this.player.options_.onEvent(category, action, JSON.stringify(labelObj));
  }

  dispose() {
    if (!this.player) return; // when player is already disposed
    super.dispose();
  }
}

videojs.registerPlugin(PLUGIN_NAME, Tracker);

export type TrackerFactoryFn = (options?: Options) => Tracker;

export default Tracker;
